/**
 * DeferredLanguageNavigator - Handles language switch navigation
 * 
 * This component MUST be rendered inside BrowserRouter.
 * It processes pending language switches by looking up translated slugs
 * directly from the database using useTranslatedSlug.
 * 
 * Part of INV-4 (identity-based language switching).
 * 
 * INVARIANTS:
 * - INV-5: Pending switches expire after LANGUAGE_SWITCH_TTL_MS
 * - INV-6: Pending switches cleared on manual navigation
 * - INV-9: Expert vanity URLs don't change on language switch
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRouteEntitySafe } from '@/contexts/RouteEntityContext';
import { LANGUAGE_SWITCH_TTL_MS, FUNCTIONAL_ROUTE_IDS } from '@/constants/routing';
import { fetchTranslatedSlug } from '@/hooks/useTranslatedSlug';
import { logger } from '@/utils/logger';

export const DeferredLanguageNavigator: React.FC = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const routeEntityContext = useRouteEntitySafe();
  
  // Track previous path for manual navigation detection (INV-6)
  const previousPathRef = useRef(routerLocation.pathname);
  // Prevent concurrent processing
  const isProcessingRef = useRef(false);
  
  // Manual navigation guard (INV-6)
  // If user clicks a link while language switch is pending, cancel the switch
  useEffect(() => {
    const pendingSwitch = routeEntityContext?.pendingLanguageSwitch;
    
    if (pendingSwitch && routerLocation.pathname !== previousPathRef.current) {
      if (process.env.NODE_ENV === 'development') {
        logger.debug('[DeferredLanguageNavigator] Manual navigation detected, clearing pending switch', {
          previousPath: previousPathRef.current,
          newPath: routerLocation.pathname,
        });
      }
      routeEntityContext.setPendingLanguageSwitch(null);
    }
    
    previousPathRef.current = routerLocation.pathname;
  }, [routerLocation.pathname, routeEntityContext]);
  
  // Deferred navigation for language switching (INV-4)
  useEffect(() => {
    const pendingSwitch = routeEntityContext?.pendingLanguageSwitch;
    
    if (!pendingSwitch || isProcessingRef.current) {
      return;
    }
    
    // TTL enforcement (INV-5)
    const age = Date.now() - pendingSwitch.createdAt;
    if (age > LANGUAGE_SWITCH_TTL_MS) {
      if (process.env.NODE_ENV === 'development') {
        logger.warn('[DeferredLanguageNavigator] Pending switch expired', {
          targetLocale: pendingSwitch.targetLocale,
          ageMs: age,
          ttlMs: LANGUAGE_SWITCH_TTL_MS,
        });
      }
      routeEntityContext.setPendingLanguageSwitch(null);
      return;
    }
    
    const { entityType, entitySourceId, metadata, targetLocale } = pendingSwitch;
    
    // Mark as processing to prevent concurrent calls
    isProcessingRef.current = true;
    
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[DeferredLanguageNavigator] Processing pending language switch', {
        targetLocale,
        entityType,
        entitySourceId,
        metadata,
      });
    }
    
    // Process the switch asynchronously
    const processSwitch = async () => {
      let targetPath: string | null = null;
      
      try {
        switch (entityType) {
          case 'home':
            targetPath = '/';
            break;
            
          case 'route':
            if (entitySourceId) {
              // Functional routes always use EN slug — no translation lookup
              if (FUNCTIONAL_ROUTE_IDS.has(entitySourceId)) {
                targetPath = `/${entitySourceId}`;
              } else {
                const slug = await fetchTranslatedSlug('route', entitySourceId, targetLocale);
                if (slug) targetPath = `/${slug}`;
              }
            }
            break;
            
          case 'page':
            if (entitySourceId) {
              const slug = await fetchTranslatedSlug('page', entitySourceId, targetLocale);
              if (slug) targetPath = `/${slug}`;
            }
            break;
            
          case 'location':
            if (entitySourceId) {
              const slug = await fetchTranslatedSlug('location', entitySourceId, targetLocale);
              if (slug) targetPath = `/${slug}`;
            }
            break;
            
          case 'expert':
            // Expert vanity URLs are locale-invariant (INV-9): the resolver
            // returns canonical_path = '/{vanity_url}' for every one of the 36
            // locales. entitySourceId is the user's UUID — it identifies the
            // entity for content refetch, NOT the URL. Reconstructing the path
            // from it produced `/{uuid}` (the locale-switch UUID-redirect bug).
            // Leave targetPath null so we DON'T navigate: the current vanity URL
            // stays, and the locale state change refetches localized content in
            // place. (Line ~164 already excludes 'expert' from the
            // no-translated-slug fallback — staying put is the intended design.)
            targetPath = null;
            break;
            
          case 'dynamic':
            // Dynamic entities (topics, publications) handling
            if (metadata?.topicSlug) {
              // Topics: translate the prefix
              // Prefix and topic name in the new language; the topic's own
              // (source) slug is the fallback when no translation exists.
              const [topicsPrefix, topicName] = await Promise.all([
                fetchTranslatedSlug('route', 'topics', targetLocale),
                metadata.topicId
                  ? fetchTranslatedSlug('topic', metadata.topicId, targetLocale)
                  : Promise.resolve(null),
              ]);
              targetPath = `/${topicsPrefix || 'topics'}/${topicName || metadata.topicSlug}`;
            } else if (metadata?.isPublication && metadata?.publicationId && metadata?.authorSlug) {
              // Publications: look up translated slug
              const publicationSlug = await fetchTranslatedSlug('publication', metadata.publicationId, targetLocale);
              if (publicationSlug) {
                targetPath = `/${metadata.authorSlug}/${publicationSlug}`;
              }
              // If no translated slug, stay on current URL
            }
            break;
        }
      } catch (err) {
        logger.warn('[DeferredLanguageNavigator] Failed to lookup translation', err);
      }
      
      // Clear pending switch BEFORE navigating to prevent loops
      routeEntityContext.setPendingLanguageSwitch(null);
      isProcessingRef.current = false;
      
      if (targetPath) {
        if (process.env.NODE_ENV === 'development') {
          logger.debug('[DeferredLanguageNavigator] Navigating to translated path', {
            targetPath,
            entityType,
            entitySourceId,
          });
        }
        navigate(targetPath, { replace: true });
      } else if (entityType !== 'home' && entityType !== 'expert') {
        // Fallback: When slug lookup fails (e.g., same slug in both locales),
        // stay on current URL. The i18n change already updated UI translations.
        logger.info('[DeferredLanguageNavigator] No translated slug found, staying on current URL', {
          entityType,
          entitySourceId,
          targetLocale,
        });
      }
    };
    
    processSwitch();
  }, [routeEntityContext?.pendingLanguageSwitch, routeEntityContext, navigate]);
  
  // This component doesn't render anything
  return null;
};

export default DeferredLanguageNavigator;
